'use client';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

// Define the JobportalOffersFilter type
export type JobportalOffersFilter = {
  selectedOffer?: string;
  search?: string;
  min_salary?: string;
  max_salary?: string;
  categories?: string[];
  employment_types?: string[];
  salary_types?: string[];
  working_hours?: string[];
  driving_licenses?: string[];
  language?: string[];
  city?: string[];
  distance?: string[];
  order?: 'asc' | 'desc';
  orderBy?: 'created_at' | 'published_at' | 'salary' | 'distance' | 'id';
  status?: 'live' | 'draft' | 'archive';
  is_paid?: boolean;
  topped_up_to?: string;
  expires_at?: string;
  dual_education?: boolean;
};

// Define the store state
type JobportalOffersStoreState = {
  filter: JobportalOffersFilter;
  autoUpdateSearchParams: boolean;
  clearTrigger: boolean;
  salaryRanges?: {
    '1': { min: string; max: string };
    '2': { min: string; max: string };
    overall: { min: string; max: string };
  };
};

// Define the store actions
type JobportalOffersStoreActions = {
  clearFilters: () => void;
  setFilter: (filter: Partial<JobportalOffersFilter>) => void;
  setAutoUpdateSearchParams: (value: boolean) => void;
  applyFilter: (filter: JobportalOffersFilter) => void;
  setSalaryRanges: (salaryRanges: JobportalOffersStoreState['salaryRanges']) => void;
};

// Create the context store
const [JobportalOffersStoreProvider, useJobportalOffersStore] = createContextStore<
  JobportalOffersStoreState,
  JobportalOffersStoreActions
>(
  {
    filter: {},
    autoUpdateSearchParams: true,
    clearTrigger: false,
  },
  set => ({
    clearFilters: () => {
      set(state => {
        state.filter = {};
        state.clearTrigger = !state.clearTrigger;
      });
    },
    setFilter: filter => {
      set(state => {
        state.filter = {
          ...state.filter,
          ...filter,
        };
      });
    },
    setAutoUpdateSearchParams: value => {
      set(state => {
        state.autoUpdateSearchParams = value;
      });
    },
    applyFilter: (filter: JobportalOffersFilter) => {
      const pathname = window.location.pathname;
      const newSearchParams = new URLSearchParams();

      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          newSearchParams.set(key, value.toString());
        }
      });

      const searchString = newSearchParams.toString();
      window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
    },
    setSalaryRanges: (salaryRanges: JobportalOffersStoreState['salaryRanges']) => {
      set(state => {
        state.salaryRanges = salaryRanges;
      });
    },
  }),
);

// JobportalOffersFilterParamsSync component
function JobportalOffersFilterParamsSync() {
  const filter = useJobportalOffersStore(state => state.filter);
  const autoUpdateSearchParams = useJobportalOffersStore(state => state.autoUpdateSearchParams);

  const pathname = usePathname();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update URL parameters when the filter changes
  useEffect(() => {
    if (!isMounted) return;
    if (!autoUpdateSearchParams) return; // Check the switch

    const newSearchParams = new URLSearchParams();

    Object.entries(filter).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        typeof value !== 'boolean' &&
        (typeof value !== 'string' || value.length > 0) &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        newSearchParams.set(key, value.toString());
      }
    });

    const searchString = newSearchParams.toString();
    window.history.replaceState({}, '', `${pathname}${searchString ? `?${searchString}` : ''}`);
  }, [filter, isMounted, pathname, autoUpdateSearchParams]);

  return null;
}

// Export the components
export { JobportalOffersStoreProvider, useJobportalOffersStore, JobportalOffersFilterParamsSync };

function buildJobOffersFilterQuery({
  selected_offer_id,
  categories,
  employment_types,
  min_salary,
  max_salary,
  salary_type,
  working_hours,
  driving_licenses,
  language,
  status,
  is_paid,
  topped_up_to,
  tags,
}: {
  selected_offer_id?: number;
  categories?: (string | number)[];
  employment_types?: (string | number)[];
  min_salary?: number;
  max_salary?: number;
  salary_type?: string[];
  working_hours?: string[];
  driving_licenses?: string[];
  language?: string[];
  status?: string;
  is_paid?: boolean;
  topped_up_to?: string;
  tags?: string[];
}) {
  const conditions: string[] = [];

  if (status !== undefined) {
    conditions.push(`status IN ['${status}']`);
  }

  if (is_paid !== undefined) {
    conditions.push(`is_paid = ${is_paid}`);
  }

  if (selected_offer_id !== undefined) {
    conditions.push(`selected_offer_id = ${selected_offer_id}`);
  }

  if (categories !== undefined && categories.length > 0) {
    conditions.push(`categories IN [${categories.map(id => `${id}`).join(', ')}]`);
  }

  if (employment_types !== undefined && employment_types.length > 0) {
    conditions.push(`employment_type_id IN [${employment_types.map(id => `${id}`).join(', ')}]`);
  }

  if (min_salary !== undefined) {
    conditions.push(`salary >= ${min_salary}`);
  }

  if (max_salary !== undefined) {
    conditions.push(`salary <= ${max_salary}`);
  }

  if (salary_type !== undefined && salary_type.length > 0) {
    conditions.push(`salary_type_id IN [${salary_type.map(id => `${id}`).join(', ')}]`);
  }

  if (working_hours !== undefined && working_hours.length > 0) {
    conditions.push(`working_hours IN [${working_hours.map(id => `${id}`).join(', ')}]`);
  }

  if (driving_licenses !== undefined && driving_licenses.length > 0) {
    conditions.push(`driving_licenses IN [${driving_licenses.map(id => `${id}`).join(', ')}]`);
  }

  if (language !== undefined && language.length > 0) {
    conditions.push(`languages IN [${language.map(id => `${id}`).join(', ')}]`);
  }

  if (topped_up_to !== undefined) {
    conditions.push(`topped_up_to >= '${topped_up_to}'`);
  }

  if (tags !== undefined && tags.length > 0) {
    conditions.push(`tags IN [${tags.map(id => `${id}`).join(', ')}]`);
  }

  return conditions.join(' AND ');
}

export const buildJobOffersQuery = (params: JobportalOffersFilter) => {
  const result: any = {
    filter: {},
  };

  if (params.status) {
    result.filter.status = params.status;
  }

  if (params.is_paid) {
    result.filter.is_paid = params.is_paid;
  }

  if (params.min_salary) {
    result.salary_from = params.min_salary;
  }

  if (params.max_salary) {
    result.salary_to = params.max_salary;
  }

  if (params.categories && params.categories.length > 0) {
    result.filter.categories = params.categories;
  }

  if (params.employment_types && params.employment_types.length > 0) {
    result.filter.employment_types = params.employment_types;
  }

  if (params.salary_types && params.salary_types.length > 0) {
    result.filter.salary_types = params.salary_types;
  }

  if (params.working_hours && params.working_hours.length > 0) {
    result.filter.working_hours = params.working_hours;
  }

  if (params.topped_up_to) {
    result.filter.topped_up_to = params.topped_up_to;
  }

  if (params.driving_licenses && params.driving_licenses.length > 0) {
    result.filter.driving_licenses = params.driving_licenses;
  }

  if (params.language && params.language.length > 0) {
    result.filter.language = params.language;
  }

  if (params.dual_education) {
    result.filter.tags = ['4']; // Assuming '4' is the tag ID for dual education
  }

  result.filter = buildJobOffersFilterQuery(result.filter);

  return result;
};
